import { useState, useCallback, useEffect, useRef } from 'react';
import type { BindingUser, BindingUsersResponse } from '../types/parcelling.types';
import { UserRole } from '@practium/shared';

type FetchUsersFn = (query: string) => Promise<BindingUsersResponse>;

interface UseBindingUsersOptions {
	userRole: UserRole;
	fetchUsers: FetchUsersFn;
	enabled?: boolean;
}

const DEBOUNCE_MS = 300;

const canBindOtherUsers = (role: UserRole): boolean => {
	return role === UserRole.Admin || role === UserRole.Company || role === UserRole.Consultant;
};

export const useBindingUsers = ({ userRole, fetchUsers, enabled = true }: UseBindingUsersOptions) => {
	const [users, setUsers] = useState<BindingUser[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const canBind = canBindOtherUsers(userRole) && enabled;

	const search = useCallback(
		async (query: string) => {
			if (!canBind) return;

			setIsLoading(true);
			try {
				const response = await fetchUsers(query);
				setUsers(response.docs);
			} catch {
				setUsers([]);
			} finally {
				setIsLoading(false);
			}
		},
		[canBind, fetchUsers]
	);

	useEffect(() => {
		if (!canBind) return;
		search('');
	}, [canBind, search]);

	const handleSearch = useCallback(
		(term: string) => {
			setSearchTerm(term);

			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}

			debounceRef.current = setTimeout(() => {
				search(term);
			}, DEBOUNCE_MS);
		},
		[search]
	);

	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, []);

	const getUserById = useCallback(
		(id: string): BindingUser | undefined => {
			return users.find((u) => u._id === id);
		},
		[users]
	);

	return {
		users,
		isLoading,
		searchTerm,
		setSearchTerm: handleSearch,
		canBind,
		getUserById,
	};
};
